import React, { useEffect, useState, useContext, useCallback } from "react"
import "./App.css"
import { IonApp, setupIonicReact } from "@ionic/react"
import { IonReactRouter } from "@ionic/react-router"
import { UserContext } from "./components/UserContext"
import { DatabaseContext } from "./components/DatabaseContext"
import { AuthContext } from "./components/AuthContext"
import Tabs from "./components/Tabs"
import _ from "lodash"
import LogIn from "./pages/LogIn"
import { useStatus } from "@capacitor-community/react-hooks/network"
import { useServiceWorker } from "./components/ServiceWorkerContext"

// Core CSS required for Ionic components to work properly
import "@ionic/react/css/core.css"

// Basic CSS for apps built with Ionic
import "@ionic/react/css/normalize.css"
import "@ionic/react/css/structure.css"
import "@ionic/react/css/typography.css"

// Optional CSS utils that can be commented out
import "@ionic/react/css/padding.css"
import "@ionic/react/css/float-elements.css"
import "@ionic/react/css/text-alignment.css"
import "@ionic/react/css/text-transformation.css"
import "@ionic/react/css/flex-utils.css"
import "@ionic/react/css/display.css"

// Theme variables
import "./theme/variables.css"

// Theme setup
setupIonicReact({
	rippleEffect: false,
	mode: "md", // Lock the material design mode
})

const App = () => {
	// Databases
	const databases = useContext(DatabaseContext)

	// Loading state for updating the UI
	const [isTicketsLoading, setIsTicketsLoading] = useState(false)
	const [isPlacesLoading, setIsPlacesLoading] = useState(false)
	const [isContactsLoading, setIsContactsLoading] = useState(false)
	const [isAccessoriesLoading, setIsAccessoriesLoading] = useState(false)

	// Update Service Worker on launch
	const serviceWorker = useServiceWorker()
	const getRegistration = useCallback(async () => {
		if ("serviceWorker" in navigator) {
			const promise = Promise.resolve(await navigator.serviceWorker.getRegistration())
			promise
				.then((result) => {
					console.log("Service Worker registration: ", result)
					if (result && result.waiting) {
						serviceWorker.updateAssets()
					}
				})
				.catch((err) => console.log(err))
		}
	}, [serviceWorker])

	useEffect(() => {
		if (serviceWorker) {
			getRegistration()
		}
	}, [getRegistration, serviceWorker])

	// IsAuth
	const [isAuth, setIsAuth] = useContext(AuthContext)

	// Network status
	const { networkStatus } = useStatus()
	const [status, setStatus] = useState(null)
	useEffect(() => {
		if (networkStatus?.connected === true) {
			setStatus(true)
		} else {
			setStatus(false)
		}
	}, [networkStatus])

	// Fetch user data
	const [user, setUser] = useContext(UserContext)
	const username = window.sessionStorage.getItem("username")
	useEffect(() => {
		if (username && _.isEmpty(user)) {
			setUser({ name: username })
		}
	}, [username, user, setUser])

	// Fetch user metadata
	useEffect(() => {
		if (user.name && _.isEmpty(user.metadata)) {
			databases.locals.contacts
				.get(username)
				.then((result) => {
					setUser({
						name: username,
						roles: result.roles,
						metadata: {
							...user.metadata,
							firstname_lastname: result.name,
							email: result.email,
							phone_number: result.phone_number,
							title: result.title,
							company: result.company,
							area: result.area,
							auto_mode: result.auto_mode,
							dark_mode: result.dark_mode,
						},
					})
				})
				.catch((err) => {
					console.log(err)
				})
		}
	}, [user, setUser, databases, isContactsLoading, username])

	// Switch between light and dark mode
	useEffect(() => {
		if (user.metadata) {
			if (user.metadata.auto_mode) {
				const prefersDark = window.matchMedia("(prefers-color-scheme: dark)")
				if (prefersDark.matches) {
					document.body.classList.add("dark")
				} else {
					document.body.classList.remove("dark")
				}
			} else if (user.metadata.dark_mode) {
				document.body.classList.add("dark")
			} else if (!user.metadata.dark_mode) {
				document.body.classList.remove("dark")
			}
		}
	}, [user])

	// Documents to delete locally
	const [ticketsToDelete, setTicketsToDelete] = useState([])
	// Set tickets to delete
	useEffect(() => {
		if (!_.isEmpty(user)) {
			databases.locals.tickets.allDocs({ include_docs: true }).then((result) => {
				const docs = result.rows.filter((row) => {
					return (!row.id.includes("_design") && row.doc.resourced.findIndex((contact) => contact.id === user.name.replace("temp-", "")) === -1) || row.doc.is_active === false
				})
				setTicketsToDelete(docs)
			})
		}
	}, [user, isTicketsLoading, databases])

	// Remove unneccessary tickets from local database
	useEffect(() => {
		if (databases.locals.tickets && ticketsToDelete.length > 0 && !_.isEmpty(user)) {
			const docs = ticketsToDelete.map((row) => {
				return {
					_id: row.doc._id,
					_rev: row.doc._rev,
					_deleted: true,
				}
			})
			databases.locals.tickets.bulkDocs(docs).then((res) => {
				if (res.ok) {
					setTicketsToDelete([])
				}
			})
		}
	}, [ticketsToDelete, databases.locals.tickets, user, isTicketsLoading])

	// Confirm logout via CouchDB /_session before tearing down the session in response
	// to a sync-stream 401. Live replications can momentarily get a 401 while CouchDB
	// re-evaluates per-DB membership (e.g. when an admin assigns a new ticket); the
	// stream retries and recovers on its own, so a transient stream error must not
	// log the user out — only an actual session loss should.
	const verifyAndHandle401 = useCallback(
		async (label, err) => {
			console.log(`Unhandled error in ${label}:`, err)
			if (err.status !== 401) return
			try {
				const sessionUrl = window.location.host.includes("localhost")
					? "https://db.linkapp-staging.taiste.fi/_session"
					: `https://${window.location.host.replace("app", "db")}/_session`
				const res = await fetch(sessionUrl, { credentials: "include" })
				const body = await res.json()
				if (!body?.userCtx?.name) {
					setIsAuth(false)
				}
			} catch (sessionErr) {
				console.log("Session probe failed:", sessionErr)
			}
		},
		[setIsAuth]
	)

	// Sync databases
	useEffect(() => {
		if (user.name) {
			databases.remotes.tickets.replicate
				.to(databases.locals.tickets, {
					live: true,
					retry: true,
				})
				.on("change", (change) => {
					setIsTicketsLoading(true)
					setTimeout(() => setIsTicketsLoading(false), 1000)
				})
				.on("error", (err) => verifyAndHandle401("tickets db", err))

			databases.locals.tickets.setMaxListeners(20)
			databases.locals.tickets.replicate
				.to(databases.remotes.tickets, {
					live: true,
					retry: true,
					filter: (doc) => {
						return !doc._deleted
					},
				})
				.on("change", (change) => {
					setIsTicketsLoading(true)
					setTimeout(() => setIsTicketsLoading(false), 1000)
				})
				.on("error", (err) => verifyAndHandle401("tickets db (push)", err))

			databases.locals.places.setMaxListeners(20)
			databases.locals.places
				.sync(databases.remotes.places, {
					live: true,
					retry: true,
				})
				.on("change", (change) => {
					setIsPlacesLoading(true)
					setTimeout(() => setIsPlacesLoading(false), 1000)
				})
				.on("error", (err) => verifyAndHandle401("places db", err))

			databases.locals.warehouses.setMaxListeners(20)
			databases.locals.warehouses
				.sync(databases.remotes.warehouses, {
					live: true,
					retry: true,
				})
				.on("change", (change) => {
					setIsPlacesLoading(true)
					setTimeout(() => setIsPlacesLoading(false), 1000)
				})
				.on("error", (err) => verifyAndHandle401("warehouses db", err))

			databases.locals.studios.setMaxListeners(20)
			databases.locals.studios
				.sync(databases.remotes.studios, {
					live: true,
					retry: true,
				})
				.on("change", (change) => {
					setIsPlacesLoading(true)
					setTimeout(() => setIsPlacesLoading(false), 1000)
				})
				.on("error", (err) => verifyAndHandle401("studios db", err))

			databases.locals.contacts.setMaxListeners(20)
			databases.locals.contacts
				.sync(databases.remotes.contacts, {
					live: true,
					retry: true,
				})
				.on("change", (change) => {
					setIsContactsLoading(true)
					setTimeout(() => setIsContactsLoading(false), 1000)
				})
				.on("error", (err) => verifyAndHandle401("contacts db", err))

			databases.remotes.accessories.replicate
				.to(databases.locals.accessories, {
					live: true,
					retry: true,
				})
				.on("change", (change) => {
					setIsAccessoriesLoading(true)
					setTimeout(() => setIsAccessoriesLoading(false), 1000)
				})
				.on("error", (err) => verifyAndHandle401("accessories db", err))

			databases.remotes.ticket_history.replicate
				.to(databases.locals.ticket_history, {
					live: true,
					retry: true,
				})
				.on("change", (change) => {
					setIsTicketsLoading(true)
					setTimeout(() => setIsTicketsLoading(false), 1000)
				})
				.on("error", (err) => verifyAndHandle401("ticket history db", err))

			databases.remotes.fm_services.replicate
				.to(databases.locals.fm_services, {
					live: true,
					retry: true,
				})
				.on("change", (change) => {
					setIsPlacesLoading(true)
					setTimeout(() => setIsPlacesLoading(false), 1000)
				})
				.on("error", (err) => verifyAndHandle401("fm services db", err))
		}
	}, [databases, user, verifyAndHandle401])

	return (
		<IonApp>
			<IonReactRouter>
				{!_.isEmpty(user) ? (
					<Tabs isTicketsLoading={isTicketsLoading} isPlacesLoading={isPlacesLoading} isContactsLoading={isContactsLoading} isAccessoriesLoading={isAccessoriesLoading} />
				) : (
					<LogIn />
				)}
			</IonReactRouter>
		</IonApp>
	)
}

export default App
